
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Percent, Eye, MessageSquare, Users, Video } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line } from 'recharts';

interface LiveStreamingSectionProps {
  timeRange: string;
}

const LiveStreamingSection: React.FC<LiveStreamingSectionProps> = ({ timeRange }) => {
  // Generate live streaming data based on time range
  const viewershipData = generateViewershipData(timeRange);
  const engagementData = generateEngagementData(timeRange);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="bg-card/95 border-border/50">
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2 text-white/95">
            <Eye className="h-5 w-5 text-soundcharts-purple" />
            Live Stream Viewership
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={viewershipData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="name" stroke="rgba(255,255,255,0.7)" fontSize={12} />
                <YAxis stroke="rgba(255,255,255,0.7)" fontSize={12} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'rgba(0,0,0,0.8)', 
                    borderColor: 'rgba(255,255,255,0.1)',
                    borderRadius: '8px',
                    color: 'rgba(255,255,255,0.9)'
                  }} 
                />
                <Bar dataKey="viewers" fill="#9b87f5" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
      
      <Card className="bg-card/95 border-border/50">
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2 text-white/95">
            <MessageSquare className="h-5 w-5 text-soundcharts-purple" />
            Viewer Engagement
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={engagementData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="name" stroke="rgba(255,255,255,0.7)" fontSize={12} />
                <YAxis stroke="rgba(255,255,255,0.7)" fontSize={12} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'rgba(0,0,0,0.8)', 
                    borderColor: 'rgba(255,255,255,0.1)',
                    borderRadius: '8px',
                    color: 'rgba(255,255,255,0.9)'
                  }} 
                />
                <Line type="monotone" dataKey="comments" stroke="#9b87f5" strokeWidth={2} activeDot={{ r: 8 }} />
                <Line type="monotone" dataKey="likes" stroke="#6E59A5" strokeWidth={2} activeDot={{ r: 8 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
      
      <Card className="bg-card/95 border-border/50 lg:col-span-2">
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2 text-white/95">
            <Users className="h-5 w-5 text-soundcharts-purple" />
            Live Streaming Performance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="border border-soundcharts-purple/30 bg-black/20 rounded-md p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-white/70">Avg. Viewers</span>
                <Users className="h-4 w-4 text-soundcharts-purple" />
              </div>
              <div className="text-xl font-semibold text-white/95">128</div>
              <div className="flex items-center gap-1 text-xs mt-1 text-green-500">
                <Percent className="h-3 w-3" />
                <span>+18% from last {timeRange}</span>
              </div>
            </div>
            
            <div className="border border-soundcharts-purple/30 bg-black/20 rounded-md p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-white/70">Watch Time</span>
                <Video className="h-4 w-4 text-soundcharts-purple" />
              </div>
              <div className="text-xl font-semibold text-white/95">842 hrs</div>
              <div className="flex items-center gap-1 text-xs mt-1 text-green-500">
                <Percent className="h-3 w-3" />
                <span>+24% from last {timeRange}</span>
              </div>
            </div>
            
            <div className="border border-soundcharts-purple/30 bg-black/20 rounded-md p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-white/70">Engagement Rate</span>
                <MessageSquare className="h-4 w-4 text-soundcharts-purple" />
              </div>
              <div className="text-xl font-semibold text-white/95">6.8%</div>
              <div className="flex items-center gap-1 text-xs mt-1 text-green-500">
                <Percent className="h-3 w-3" />
                <span>+3.2% from last {timeRange}</span>
              </div>
            </div>
            
            <div className="border border-soundcharts-purple/30 bg-black/20 rounded-md p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-white/70">Retention Rate</span>
                <Users className="h-4 w-4 text-soundcharts-purple" />
              </div>
              <div className="text-xl font-semibold text-white/95">62%</div>
              <div className="flex items-center gap-1 text-xs mt-1 text-green-500">
                <Percent className="h-3 w-3" />
                <span>+5% from last {timeRange}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Helper functions to generate mock data
const generateViewershipData = (timeRange: string) => {
  let dataPoints = [];
  const baseViewers = 100;
  
  switch (timeRange) {
    case 'week':
      dataPoints = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, index) => ({
        name: day,
        viewers: baseViewers + Math.floor(Math.random() * 150) + (index * 10)
      }));
      break;
    case 'month':
      dataPoints = ['Week 1', 'Week 2', 'Week 3', 'Week 4'].map((week, index) => ({
        name: week,
        viewers: baseViewers + Math.floor(Math.random() * 200) + (index * 30)
      }));
      break;
    case 'year':
      dataPoints = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((month, index) => ({
        name: month,
        viewers: baseViewers + Math.floor(Math.random() * 300) + (index * 15)
      }));
      break;
    default:
      dataPoints = ['2020', '2021', '2022', '2023', '2024'].map((year, index) => ({
        name: year,
        viewers: baseViewers + Math.floor(Math.random() * 500) + (index * 100)
      }));
  }
  
  return dataPoints;
};

const generateEngagementData = (timeRange: string) => {
  let dataPoints = [];
  const baseComments = 20;
  const baseLikes = 50;
  
  switch (timeRange) {
    case 'week':
      dataPoints = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, index) => ({
        name: day,
        comments: baseComments + Math.floor(Math.random() * 30) + (index * 2),
        likes: baseLikes + Math.floor(Math.random() * 70) + (index * 5)
      }));
      break;
    case 'month':
      dataPoints = ['Week 1', 'Week 2', 'Week 3', 'Week 4'].map((week, index) => ({
        name: week,
        comments: baseComments + Math.floor(Math.random() * 40) + (index * 5),
        likes: baseLikes + Math.floor(Math.random() * 100) + (index * 15)
      }));
      break;
    case 'year':
      dataPoints = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((month, index) => ({
        name: month,
        comments: baseComments + Math.floor(Math.random() * 60) + (index * 3),
        likes: baseLikes + Math.floor(Math.random() * 150) + (index * 10)
      }));
      break;
    default:
      dataPoints = ['2020', '2021', '2022', '2023', '2024'].map((year, index) => ({
        name: year,
        comments: baseComments + Math.floor(Math.random() * 100) + (index * 20),
        likes: baseLikes + Math.floor(Math.random() * 200) + (index * 50)
      }));
  }
  
  return dataPoints;
};

export default LiveStreamingSection;
